import { Link, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {
  ArrowRight,
  TrendingUp,
  BookOpen,
  Briefcase,
  Lightbulb,
  Star,
  Zap,
  BarChart3,
  FolderKanban,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Users2,
  Rocket,
  GraduationCap,
  Compass,
  ShoppingCart
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { ROUTES, USER_ROLES } from '@constants';
import Button from '@components/common/Button';
import Card from '@components/common/Card';
import PricingPlans from '@components/common/PricingPlans';

// Import feature images
import careerDashboardImg from '@images/career_journy_report.png';
import portfolioBuilderImg from '@images/portfolio_builder.webp';
import mentorConnectImg from '@images/mentor_connect.png';
import skillDevelopmentImg from '@images/skill_development.png';
import jobOpportunitiesImg from '@images/job_opportunities.png';
import cartImg from '@images/cart.png';

// Import hero image
import heroImage from '@images/personalized_career_roadmap.png';

/**
 * ── Shared Typography Scale ──
 * Single source of truth so every heading/description across the page
 * uses the same size steps and the same responsive breakpoints.
 * Adjust here once, it reflects everywhere.
 */
const TYPE = {
  // The one big hero line (rotating text)
  hero: 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight',
  heroSub: 'text-base sm:text-lg md:text-xl leading-relaxed',

  // Main section headings ("Everything You Need", "Why Choose F.O.W", etc.)
  sectionHeading: 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight',
  sectionSub: 'text-sm sm:text-base md:text-lg leading-relaxed',

  // Sub-section / card headings (feature titles, benefit titles, differentiator titles)
  cardHeading: 'text-lg sm:text-xl md:text-2xl font-bold leading-snug',
  cardHeadingSm: 'text-base sm:text-lg font-bold leading-snug',

  // Body copy inside cards/features
  body: 'text-sm sm:text-base md:text-lg leading-relaxed',
  bodySm: 'text-xs sm:text-sm leading-relaxed',

  // Stats
  statNumber: 'text-2xl sm:text-4xl md:text-5xl font-bold leading-none whitespace-nowrap',
  statLabel: 'text-[11px] sm:text-base md:text-lg leading-snug',
};

// Floating info cards that orbit the hero dashboard mockup (desktop/tablet only —
// on small screens they're dropped so the hero image stays clean and full-width).
const heroFloatingCards = [
  {
    position: 'absolute -top-5 -left-5 xl:-top-6 xl:-left-8',
    icon: BarChart3,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    title: 'Track Your Progress',
    subtitle: 'Set goals and achieve more',
    delay: '0s'
  },
  {
    position: 'absolute -top-5 -right-5 xl:-top-6 xl:-right-8',
    icon: GraduationCap,
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    title: 'Explore Opportunities',
    subtitle: 'Find your perfect path',
    delay: '0.6s'
  },
  {
    position: 'absolute -bottom-5 -left-5 xl:-bottom-6 xl:-left-8',
    icon: Compass,
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-600',
    title: 'Get Expert Guidance',
    subtitle: 'Learn from industry mentors',
    delay: '1.2s'
  },
  {
    position: 'absolute -bottom-5 -right-5 xl:-bottom-6 xl:-right-8',
    icon: Lightbulb,
    iconBg: 'bg-sky-50',
    iconColor: 'text-sky-600',
    title: 'Build In-Demand Skills',
    subtitle: 'Stay ahead with the right skills',
    delay: '1.8s'
  }
];

const HomePage = () => {
  const { isAuthenticated, user } = useAuth();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [heroVisible, setHeroVisible] = useState({});
  const heroRefs = useRef({});

  const rotatingTexts = [
    'Connect with Mentors',
    'Land Your Dream Job',
    'Build Your Skills',
    'Chart Your Roadmap',
    'Grow Your Career',
  ];

  useEffect(() => {
    setIsVisible(true);

    const interval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % rotatingTexts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observers = {};
    
    Object.keys(heroRefs.current).forEach(key => {
      if (heroRefs.current[key]) {
        observers[key] = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setHeroVisible(prev => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.1 }
        );
        observers[key].observe(heroRefs.current[key]);
      }
    });

    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, []);

  const setHeroRef = (key) => (el) => {
    heroRefs.current[key] = el;
  };

  // Redirect authenticated users to appropriate dashboard
  if (isAuthenticated) {
    if (user?.role === USER_ROLES.ADMIN) {
      return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
    }
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  const features = [
    {
      icon: BarChart3,
      title: 'Career Dashboard',
      description: 'Get a 360° view of your career journey with clarity scores, skill trackers, and personalized roadmaps — all in one place.',
      image: careerDashboardImg,
      imageAlt: 'Career Dashboard Interface'
    },
    {
      icon: FolderKanban,
      title: 'Portfolio Builder',
      description: 'Showcase your skills, certifications, experience, and projects in a professional portfolio that stands out to recruiters.',
      image: portfolioBuilderImg,
      imageAlt: 'Portfolio Builder Interface'
    },
    {
      icon: MessageCircle,
      title: 'Mentor Connect',
      description: 'Chat directly with industry mentors who guide you through career decisions, interview prep, and growth strategies.',
      image: mentorConnectImg,
      imageAlt: 'Mentor Connect Chat Interface'
    },
    {
      icon: TrendingUp,
      title: 'Skill Development',
      description: 'Track your progress and develop in-demand skills for your dream career with personalized learning paths.',
      image: skillDevelopmentImg,
      imageAlt: 'Skill Development Tracker'
    },
    {
      icon: Briefcase,
      title: 'Job Opportunities',
      description: 'Access curated job listings matched to your profile and career goals with smart recommendations.',
      image: jobOpportunitiesImg,
      imageAlt: 'Job Opportunities Board'
    },
    {
      icon: ShoppingCart,
      title: 'Order Books & Resources',
      description: 'Browse and order curated books, courses, magazines, newspapers, and study notes — all handpicked to accelerate your career growth.',
      image: cartImg,
      imageAlt: 'Order Books and Resources'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Create Your Profile',
      description: 'Sign up and tell us about your career aspirations, skills, and interests.'
    },
    {
      step: '02',
      title: 'Get Matched with Mentors',
      description: 'Our AI connects you with experienced mentors in your field of interest.'
    },
    {
      step: '03',
      title: 'Build Your Roadmap',
      description: 'Work with mentors to create a personalized career development plan.'
    },
    {
      step: '04',
      title: 'Achieve Your Goals',
      description: 'Track progress, develop skills, and land your dream career.'
    }
  ];

  const benefits = [
    {
      icon: Lightbulb,
      title: 'F.O.W',
      description: 'Discover your true calling and chart a clear path forward.'
    },
    {
      icon: Zap,
      title: 'Accelerated Growth',
      description: 'Learn from experts and fast-track your career development.'
    },
    {
      icon: BookOpen,
      title: 'Continuous Learning',
      description: 'Access resources, courses, and tools for lifelong learning.'
    }
  ];

  // Differentiation points — distinct from the Features section above,
  // focused on trust/quality rather than repeating feature names.
  const differentiators = [
    {
      icon: ShieldCheck,
      title: 'Verified Mentors Only',
      description: 'Every mentor is document-verified before they can guide you, so advice comes from genuine industry experience.',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      icon: Sparkles,
      title: 'Personalized, Not Generic',
      description: 'Recommendations adapt to your interests and strengths instead of giving everyone the same generic career list.',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700'
    },
    {
      icon: Users2,
      title: 'Built for Every Stage',
      description: 'Whether you\'re in Class 10, graduating, or switching careers mid-way, the platform adapts to where you are.',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-700'
    },
    {
      icon: BookOpen,
      title: 'Clear, Actionable Reports',
      description: 'No jargon-heavy analysis — just a clarity report you can actually act on for your next academic or career step.',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700'
    }
  ];

  const testimonials = [
    { name: 'Priya Sharma', role: 'Software Engineer', text: 'F.O.W helped me find my true calling and connected me with amazing mentors.' },
    { name: 'Rahul Verma', role: 'Product Manager', text: 'The mentorship program was invaluable. I got clarity on my career goals within weeks.' },
    { name: 'Ananya Singh', role: 'Data Scientist', text: 'The roadmap and resources provided helped me land my dream job at a top tech company.' },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">

      {/* ── Building India's No.1 Hero Section ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">

  {/* Layered background */}
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute inset-0 bg-gradient-to-br from-primary-50/60 via-white to-secondary-50/40" />

    {/* Dot grid — subtle texture */}
    <div
      className="absolute inset-0 opacity-[0.18]"
      style={{
        backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)',
        backgroundSize: '30px 30px',
        maskImage: 'radial-gradient(ellipse 60% 50% at center, black 20%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at center, black 20%, transparent 75%)',
      }}
    />

    <div className="absolute top-10 right-0 w-[28rem] h-[28rem] bg-primary-200/25 rounded-full blur-[100px]" />
    <div className="absolute bottom-0 left-0 w-[32rem] h-[32rem] bg-secondary-200/25 rounded-full blur-[110px]" />
  </div>

  <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <div className="max-w-5xl mx-auto text-center py-10 sm:py-12 md:py-14">

      <div 
        ref={setHeroRef('hero-badge')}
        className={`inline-flex items-center gap-2 mb-5 px-4 py-1.5 bg-white/90 backdrop-blur-sm rounded-full border border-primary-200 shadow-sm transition-all duration-1000 ${
          heroVisible['hero-badge'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 animate-pulse" />
        <span className="text-xs sm:text-sm font-semibold text-primary-700 tracking-wide">
          BUILT FROM REAL EXPERIENCES
        </span>
      </div>

      <h1 
        ref={setHeroRef('hero-title')}
        className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-primary-900 leading-[1.1] tracking-tight mb-6 sm:mb-8 transition-all duration-1000 delay-200 ${
          heroVisible['hero-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        Building India's{' '}
        <span className="relative inline-block">
          <span className="relative z-10 bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            No.1
          </span>
          <span className="absolute bottom-2 left-0 w-full h-3 bg-primary-200/40 -z-10"></span>
        </span>
        <br />
        Career Clarity Platform
      </h1>

      <p 
        ref={setHeroRef('hero-desc')}
        className={`text-base sm:text-lg md:text-xl text-primary-700 leading-relaxed max-w-3xl mx-auto mb-8 sm:mb-12 transition-all duration-1000 delay-400 ${
          heroVisible['hero-desc'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        From classrooms and career conversations to entrepreneurship and industry ecosystems —
        F.O.W. is being built from <span className="font-bold text-primary-900">real experiences</span>,
        <span className="font-bold text-primary-900"> real people</span> and
        <span className="font-bold text-primary-900"> real problems</span>.
      </p>

      {/* Learn Our Story Link */}
      <div 
        ref={setHeroRef('hero-button')}
        className={`flex justify-center transition-all duration-1000 delay-600 ${
          heroVisible['hero-button'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <Link 
          to={ROUTES.OUR_JOURNEY}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300"
        >
          <span>Learn Our Story</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

    </div>
  </div>

  {/* Soft shadow edge to visually close off this section */}
  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-200 to-transparent" />
</section>

      {/* ── Hero Section ── */}
      <section className="hero-section relative bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-100 py-8 sm:py-10 md:py-14 lg:py-12 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.35fr] gap-12 sm:gap-14 lg:gap-10 items-center">

            {/* Left – Text */}
            <div className="text-center lg:text-left">
              <div className="inline-block mb-3 px-3 py-1.5 bg-secondary-100 rounded-full">
                <span className="text-xs sm:text-sm font-semibold text-secondary-700">
                  🚀 Navigate Your Future
                </span>
              </div>

              <h1 className={`${TYPE.hero} text-primary-900 mb-4`}>
                Build Your Portfolio,
                <span
                  className={`block bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent transition-all duration-300 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
                  }`}
                >
                  {rotatingTexts[currentTextIndex]}
                  <span className="inline-block w-0.5 h-[0.8em] ml-1 bg-secondary-500 animate-pulse rounded-sm align-middle" />
                </span>
              </h1>

              <p className={`${TYPE.heroSub} text-primary-600 mb-6 max-w-xl mx-auto lg:mx-0`}>
                Chart a personalized roadmap to your dream career with expert guidance, skill development, and real opportunities.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
                <Link to={ROUTES.REGISTER} className="w-full sm:w-auto">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto">
                    Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={ROUTES.LOGIN} className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">
                    Sign In
                  </Button>
                </Link>
              </div>

              {/* Trust Badges - stacked icon cards; wraps to 2 lines on very small screens */}
              
            </div>

            {/* Right – Dashboard mockup */}
            <div className="hero-image-wrap w-full relative mt-4 sm:mt-8 lg:mt-0 lg:scale-105 xl:scale-110 lg:origin-left">

              {/* decorative dash marks — desktop only */}
              {/* <span className="hidden lg:block absolute -top-4 left-10 w-6 h-1.5 rounded-full bg-purple-400/70 rotate-[20deg]" aria-hidden="true" />
              <span className="hidden lg:block absolute -top-10 left-16 w-1.5 h-6 rounded-full bg-purple-400/70 -rotate-[10deg]" aria-hidden="true" />
              <span className="hidden lg:block absolute -bottom-14 right-8 w-6 h-1.5 rounded-full bg-purple-400/70 -rotate-[20deg]" aria-hidden="true" /> */}

              <div className="relative bg-white rounded-2xl shadow-xl p-2 border border-primary-200 float-soft">
                <img
                  src={heroImage}
                  alt="Career Dashboard - Your personalized career journey tracker"
                  className="w-full h-auto rounded-xl"
                  style={{ imageRendering: '-webkit-optimize-contrast' }}
                  loading="eager"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Features Section ── */}
      <section className="py-6 sm:py-8 md:py-10 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-10 md:mb-12">
            <h2 className={`${TYPE.sectionHeading} text-primary-900 mb-2 sm:mb-4`}>
              Everything You Need to Succeed
            </h2>
            <p className={`${TYPE.sectionSub} text-primary-600 max-w-3xl mx-auto`}>
              Explore the powerful features that make Career Clarity GPS your ultimate career companion.
            </p>
          </div>

          {/* Phone (<640px): compact swipeable cards — full-width alternating blocks
              don't fit well on small screens, so features become a horizontal,
              snap-scrolling carousel instead. */}
          <div className="sm:hidden">
            <div className="-mx-4 px-4 overflow-x-auto scrollbar-hide">
              <div className="flex gap-4 snap-x snap-mandatory">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className="snap-center shrink-0 w-[80%] bg-white rounded-2xl shadow-lg border border-primary-100 overflow-hidden"
                    >
                      <img
                        src={feature.image}
                        alt={feature.imageAlt}
                        loading="lazy"
                        className="w-full h-36 object-cover"
                      />
                      <div className="p-5">
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-lg flex-shrink-0">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <h3 className="text-base font-bold text-primary-900 leading-snug">
                            {feature.title}
                          </h3>
                        </div>
                        <p className="text-sm text-primary-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {features.map((_, index) => (
                <span key={index} className="w-1.5 h-1.5 rounded-full bg-secondary-300" />
              ))}
            </div>
            <p className="text-center text-xs text-primary-400 mt-2">Swipe to see more →</p>
          </div>

          {/* Tablet & desktop: full alternating image/content blocks */}
          <div className="hidden sm:block space-y-12 md:space-y-16">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={index}
                  className={`grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center ${isEven ? '' : 'lg:grid-flow-dense'
                    }`}
                >
                  {/* Image */}
                  <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="bg-white rounded-2xl shadow-2xl border border-primary-100">
                      <img
                        src={feature.image}
                        alt={feature.imageAlt}
                        loading="lazy"
                        className="w-full rounded-xl object-cover"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'} text-center lg:text-left`}>
                    <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl mb-5 sm:mb-6">
                      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>

                    <h3 className={`${TYPE.cardHeading} text-primary-900 mb-3 sm:mb-4`}>
                      {feature.title}
                    </h3>

                    <p className={`${TYPE.body} text-primary-600 max-w-md mx-auto lg:mx-0`}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works Section ── */}
      <section className="py-6 sm:py-7 md:py-8 bg-primary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className={`${TYPE.sectionHeading} text-primary-900 mb-2 sm:mb-3`}>
              How It Works
            </h2>
            <p className={`${TYPE.sectionSub} text-primary-600 max-w-xl mx-auto`}>
              Four simple steps to transform your career
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <Card className="p-4 sm:p-5 h-full">
                  <div className="text-2xl sm:text-4xl font-bold text-secondary-200 mb-2 sm:mb-3 leading-none">
                    {item.step}
                  </div>
                  <h3 className={`${TYPE.cardHeadingSm} text-primary-900 mb-1.5 sm:mb-2`}>
                    {item.title}
                  </h3>
                  <p className={`${TYPE.bodySm} text-primary-600`}>
                    {item.description}
                  </p>
                </Card>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-5 h-5 text-secondary-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits / Differentiators Section ── */}
      <section className="py-6 sm:py-8 md:py-10 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
            {/* Left Side - Why Choose F.O.W */}
            <div>
              <h2 className={`${TYPE.sectionHeading} text-primary-900 mb-2 sm:mb-4`}>
                Why Choose F.O.W?
              </h2>
              <p className={`${TYPE.sectionSub} text-primary-600 mb-5 sm:mb-8`}>
                We're more than just a platform—we're your partner in career success.
              </p>
              <div className="space-y-4 sm:space-y-6">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-xl flex items-center justify-center">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className={`${TYPE.cardHeadingSm} text-primary-900 mb-2`}>
                          {benefit.title}
                        </h3>
                        <p className={`${TYPE.bodySm} text-primary-600`}>
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side - Differentiator Cards Grid (distinct from Features section) */}
            <div className="w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {differentiators.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className={`${item.bgColor} rounded-xl p-4 sm:p-6 hover:shadow-lg transition-shadow duration-300`}
                    >
                      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${item.textColor} mb-2 sm:mb-3`} />
                      <h3 className={`${TYPE.cardHeadingSm} ${item.textColor} mb-1.5 sm:mb-2`}>
                        {item.title}
                      </h3>
                      <p className={`${TYPE.bodySm} text-gray-600`}>
                        {item.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Section ── */}
      <section className="py-5 sm:py-8 md:py-9 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-1 sm:gap-6 text-center text-white">
            {[
              { value: '10,000+', label: 'Active Students' },
              { value: '500+', label: 'Expert Mentors' },
              { value: '95%', label: 'Success Rate' },
            ].map((stat) => (
              <div key={stat.label} className="min-w-0 px-1">
                <div className={`${TYPE.statNumber} mb-1`}>{stat.value}</div>
                <div className={`${TYPE.statLabel} opacity-90`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials Section (moved before Pricing to build trust first) ── */}
      <section className="py-6 sm:py-8 md:py-10 bg-primary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className={`${TYPE.sectionHeading} text-primary-900 mb-2 sm:mb-3`}>
              Success Stories
            </h2>
            <p className={`${TYPE.sectionSub} text-primary-600 max-w-xl mx-auto`}>
              Hear from professionals who transformed their careers
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-4 sm:p-5">
                <div className="flex gap-0.5 mb-2 sm:mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className={`${TYPE.bodySm} text-primary-700 mb-3 sm:mb-4 italic`}>
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {t.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-primary-900 truncate">{t.name}</div>
                    <div className="text-xs text-primary-500 truncate">{t.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Plans Section ── */}
      <PricingPlans />

      {/* ── Final CTA Section (FAQ link merged in as a secondary line) ── */}
      <section className="py-6 sm:py-8 md:py-10 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`${TYPE.sectionHeading} text-white mb-2 sm:mb-4`}>
            Ready to Transform Your Career ?
          </h2>
          <p className={`${TYPE.sectionSub} text-white/90 mb-4 sm:mb-6 max-w-2xl mx-auto`}>
            Join thousands of professionals who have accelerated their growth with F.O.W.
          </p>
          <div className="flex flex-row items-stretch justify-center gap-2.5 sm:gap-3 mb-5 sm:mb-8">
            <Link to={ROUTES.REGISTER} className="flex-1 sm:flex-none min-w-0">
              <Button size="lg" className="w-full sm:w-auto whitespace-nowrap px-3 sm:px-6 text-[13px] sm:text-base !bg-white !text-navbar !border !border-white hover:!bg-navbar hover:!text-white">
                <span className="truncate">Start Your Journey</span> <ArrowRight className="ml-1.5 sm:ml-2 w-4 h-4 flex-shrink-0" />
              </Button>
            </Link>
            <Link to={ROUTES.LOGIN} className="flex-1 sm:flex-none min-w-0">
              <Button variant="outline" size="lg" className="w-full sm:w-auto whitespace-nowrap px-3 sm:px-6 text-[13px] sm:text-base !border !border-white !text-white !bg-transparent hover:!bg-white/10 font-semibold">
                Sign In
              </Button>
            </Link>
          </div>
          <div className="pt-4 sm:pt-6 border-t border-white/20 max-w-3xl mx-auto">
            <p className="text-sm text-white/80">
              Want to know our story?{' '}
              <Link to={ROUTES.OUR_JOURNEY} className="font-semibold text-white underline underline-offset-2 hover:text-white/90">
                Our Journey
              </Link>
              {' '} | {' '}
              Still have questions?{' '}
              <Link to={ROUTES.FAQ} className="font-semibold text-white underline underline-offset-2 hover:text-white/90">
                View our FAQs
              </Link>
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;